import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, And, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { getFormattedDisplayName, UsersService } from '../../../../data-services/users.service';
import {
  FulfillmentProjectOrderStatusChangedTrigger,
  FulfillmentProjectStatusChangedTrigger,
  FulfillmentTaskStatusChangedTrigger,
  SalesTaskStatusChangedTrigger,
} from '../../../component-loader/constants';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';

export enum TaskManagerPersonaType {
  DigitalAgent = 'digital_agent',
  Salesperson = 'sales_person',
}

interface SharedTranslationKeys {
  title: string;
  activitySubtitle: string;
  specifiedStatus: string;
  specifiedStatusAndName: string;
  specifiedStatusAndUser: string;
  specifiedAll: string;
}

const childTranslationKeys: { [taskDefinitionId: string]: SharedTranslationKeys } = {
  [FulfillmentTaskStatusChangedTrigger]: {
    title: 'AUTOMATIONS.EDITOR.TRIGGERS.TASK_STATUS_CHANGED.TASK_STATUS_TITLE',
    activitySubtitle: 'AUTOMATIONS.EDITOR.TRIGGERS.TASK_STATUS_CHANGED.ACTIVITY.ACTIVITY_SUBTITLE',
    specifiedStatus: 'AUTOMATIONS.EDITOR.TRIGGERS.TASK_STATUS_CHANGED.ACTION.SPECIFIED_STATUS',
    specifiedStatusAndName: 'AUTOMATIONS.EDITOR.TRIGGERS.TASK_STATUS_CHANGED.ACTION.SPECIFIED_STATUS_AND_NAME',
    specifiedStatusAndUser: 'AUTOMATIONS.EDITOR.TRIGGERS.TASK_STATUS_CHANGED.ACTION.SPECIFIED_STATUS_AND_USER',
    specifiedAll: 'AUTOMATIONS.EDITOR.TRIGGERS.TASK_STATUS_CHANGED.ACTION.SPECIFIED_ALL',
  },
  [FulfillmentProjectStatusChangedTrigger]: {
    title: 'AUTOMATIONS.EDITOR.TRIGGERS.FULFILLMENT_PROJECT_STATUS_CHANGED.PROJECT_STATUS_ACCOUNT_TITLE',
    activitySubtitle: 'AUTOMATIONS.EDITOR.TRIGGERS.FULFILLMENT_PROJECT_STATUS_CHANGED.ACTIVITY.ACTIVITY_SUBTITLE',
    specifiedStatus: 'AUTOMATIONS.EDITOR.TRIGGERS.FULFILLMENT_PROJECT_STATUS_CHANGED.ACTION.SPECIFIED_STATUS',
    specifiedStatusAndName:
      'AUTOMATIONS.EDITOR.TRIGGERS.FULFILLMENT_PROJECT_STATUS_CHANGED.ACTION.SPECIFIED_STATUS_AND_NAME',
    specifiedStatusAndUser:
      'AUTOMATIONS.EDITOR.TRIGGERS.FULFILLMENT_PROJECT_STATUS_CHANGED.ACTION.SPECIFIED_STATUS_AND_USER',
    specifiedAll: 'AUTOMATIONS.EDITOR.TRIGGERS.FULFILLMENT_PROJECT_STATUS_CHANGED.ACTION.SPECIFIED_ALL',
  },
  [FulfillmentProjectOrderStatusChangedTrigger]: {
    title: 'AUTOMATIONS.EDITOR.TRIGGERS.FULFILLMENT_PROJECT_STATUS_CHANGED.PROJECT_STATUS_ORDER_TITLE',
    activitySubtitle: 'AUTOMATIONS.EDITOR.TRIGGERS.FULFILLMENT_PROJECT_STATUS_CHANGED.ACTIVITY.ACTIVITY_SUBTITLE',
    specifiedStatus: 'AUTOMATIONS.EDITOR.TRIGGERS.FULFILLMENT_PROJECT_STATUS_CHANGED.ACTION.SPECIFIED_STATUS_ORDER',
    specifiedStatusAndName:
      'AUTOMATIONS.EDITOR.TRIGGERS.FULFILLMENT_PROJECT_STATUS_CHANGED.ACTION.SPECIFIED_STATUS_AND_NAME_ORDER',
    specifiedStatusAndUser:
      'AUTOMATIONS.EDITOR.TRIGGERS.FULFILLMENT_PROJECT_STATUS_CHANGED.ACTION.SPECIFIED_STATUS_AND_USER_ORDER',
    specifiedAll: 'AUTOMATIONS.EDITOR.TRIGGERS.FULFILLMENT_PROJECT_STATUS_CHANGED.ACTION.SPECIFIED_ALL_ORDER',
  },
  [SalesTaskStatusChangedTrigger]: {
    title: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_TASK_STATUS_CHANGED.TASK_STATUS_TITLE_V2',
    activitySubtitle: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_TASK_STATUS_CHANGED.ACTIVITY.ACTIVITY_SUBTITLE',
    specifiedStatus: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_TASK_STATUS_CHANGED.ACTION.SPECIFIED_STATUS',
    specifiedStatusAndName: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_TASK_STATUS_CHANGED.ACTION.SPECIFIED_STATUS_AND_NAME',
    specifiedStatusAndUser: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_TASK_STATUS_CHANGED.ACTION.SPECIFIED_STATUS_AND_USER',
    specifiedAll: 'AUTOMATIONS.EDITOR.TRIGGERS.SALES_TASK_STATUS_CHANGED.ACTION.SPECIFIED_ALL',
  },
};

@Injectable({ providedIn: 'root' })
export class TaskStatusChangedService extends AutomationDisplayService {
  constructor(
    protected readonly translateService: TranslateService,
    private readonly usersService: UsersService,
  ) {
    super();
  }

  /** Gets text for the activity column in the table. */
  getActivityColumn(_namespace: string, activity: ActivityInterface): ActivityTableEntry {
    // Check for problems with the step
    const data = activity?.outputData ? JSON.parse(activity.outputData) : {};
    if (activity?.error || !data.task_data) {
      return this.tableEntry(
        this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.TASK_STATUS_CHANGED.ACTIVITY.FAILURE'),
        this.emptyBody(),
      );
    }
    const taskDefinitionId = activity.workflowStep.waitForTriggerTaskDefinitionId
      ? activity.workflowStep.waitForTriggerTaskDefinitionId
      : activity.workflowStep.taskDefinitionId;
    const title = childTranslationKeys[taskDefinitionId].activitySubtitle;

    // Show user-friendly text if the step proceeded
    return this.tableEntry(
      this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.ENTERED_AUTOMATION'),
      this.translateService.stream(title, {
        task_name: data.task_data.name.trim(),
        status: data.task_data.status,
      }),
    );
  }

  /** The text in an automation node (i.e. the nodes in the graph). */
  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    const taskDefinitionId = step.waitForTriggerTaskDefinitionId
      ? step.waitForTriggerTaskDefinitionId
      : step.taskDefinitionId;
    let status = '';
    let taskName = '';
    let userSubjectId = '';

    if (step.choices?.rules?.length > 0) {
      const and = And.fromRuleInterface(step.choices?.rules[0]);
      const personaFromRule = and.listValuesByVariablePath('{.task_data.persona_type}');
      if (!!personaFromRule && personaFromRule.length > 0) {
        // personaType = personaFromRule[0]; // not used
      }

      const statusFromRule = and.listValuesByVariablePath('{.status}');
      if (!!statusFromRule && statusFromRule.length > 0) {
        // the status value is a list
        status = statusFromRule[0][0];
      }
      const taskNameFromRule = and.listValuesByVariablePath('{.task_data.name}');
      if (!!taskNameFromRule && taskNameFromRule.length > 0) {
        taskName = taskNameFromRule[0];
      }
      const userSubjectIdFromRule = and.listValuesByVariablePath('{.task_data.assignees}');
      if (!!userSubjectIdFromRule && userSubjectIdFromRule.length > 0) {
        // the userSubjectId value is a list
        userSubjectId = userSubjectIdFromRule[0][0];
      }
    }

    // Note there is a few different content options depending on which options this trigger has values for
    if (status) {
      let subtitle = childTranslationKeys[taskDefinitionId].specifiedStatus;

      let name$ = of('');

      if (taskName) {
        subtitle = childTranslationKeys[taskDefinitionId].specifiedStatusAndName;
      }
      if (userSubjectId) {
        subtitle = childTranslationKeys[taskDefinitionId].specifiedStatusAndUser;
        name$ = this.usersService.getUsersById([userSubjectId]).pipe(
          take(1),
          map((users) => getFormattedDisplayName(users[0])),
          catchError(() => of('')),
        );
      }
      if (!!taskName && !!userSubjectId) {
        subtitle = childTranslationKeys[taskDefinitionId].specifiedAll;
      }
      if (userSubjectId) {
        return name$.pipe(
          switchMap((userName) => {
            if (userName) {
              return this.nodeDisplayData(
                this.translateService.stream(subtitle, {
                  task_name: taskName,
                  user_name: userName,
                  status: status,
                }),
                this.noValidationErrorsDetected(),
              );
            }

            if (taskName) {
              subtitle = childTranslationKeys[taskDefinitionId].specifiedStatusAndName;
            } else {
              subtitle = childTranslationKeys[taskDefinitionId].specifiedStatus;
            }
            return this.nodeDisplayData(
              this.translateService.stream(subtitle, { task_name: taskName, status: status }),
              this.noValidationErrorsDetected(),
            );
          }),
        );
      } else {
        return this.nodeDisplayData(
          this.translateService.stream(subtitle, { task_name: taskName, status: status }),
          this.noValidationErrorsDetected(),
        );
      }
    } else {
      const validationErrors = this.validationErrorsDetected(
        step,
        this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.TASK_STATUS_CHANGED.ACTION.CONFIG_ERROR'),
        true,
      );

      return this.nodeDisplayData(
        this.translateService.stream(childTranslationKeys[taskDefinitionId].title),
        validationErrors,
      );
    }
  }
}
